if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this._mobileDotsResizeHandler = () => this.updateMobileThumbnailDots();
        window.addEventListener('resize', this._mobileDotsResizeHandler);
        this.initMobileThumbnailDots();
      }

      disconnectedCallback() {
        if (this._mobileDotsResizeHandler) {
          window.removeEventListener('resize', this._mobileDotsResizeHandler);
        }

        if (this._mobileDotsObserver) {
          this._mobileDotsObserver.disconnect();
        }
      }

      initMobileThumbnailDots() {
        const swiperComponent = this.querySelector('swiper-component');
        if (!swiperComponent) return;

        const syncDots = () => {
          this.updateMobileThumbnailDots();
          requestAnimationFrame(() => this.updateMobileThumbnailDots());
          setTimeout(() => this.updateMobileThumbnailDots(), 120);
        };

        const bindWhenReady = (retries = 60) => {
          const swiper = swiperComponent.initSwiper || swiperComponent.swiperEl?.swiper;

          if (swiper && swiper !== this._mobileDotsSwiper) {
            this._mobileDotsSwiper = swiper;
            swiper.on('init', syncDots);
            swiper.on('activeIndexChange', syncDots);
            swiper.on('realIndexChange', syncDots);
            swiper.on('slideChange', syncDots);
            swiper.on('slideChangeTransitionEnd', syncDots);
            swiper.on('transitionEnd', syncDots);
            swiper.on('update', syncDots);
          }

          syncDots();

          if (!swiper && retries > 0) {
            setTimeout(() => bindWhenReady(retries - 1), 100);
          }
        };

        bindWhenReady();

        if (!this._mobileDotsObserver) {
          const wrapper = this.querySelector('.swiper-main .swiper-wrapper');

          if (wrapper) {
            this._mobileDotsObserver = new MutationObserver(syncDots);
            this._mobileDotsObserver.observe(wrapper, {
              attributes: true,
              attributeFilter: ['class'],
              subtree: true,
            });
          }
        }
      }

      getMobileDotsActiveIndex(buttons) {
        const swiperComponent = this.querySelector('swiper-component');
        const swiper = swiperComponent?.initSwiper || swiperComponent?.swiperEl?.swiper;
        const activeSlide = this.querySelector('.swiper-main .swiper-slide-active');
        const activeSlideIndex = Number.parseInt(activeSlide?.dataset.swiperSlideIndex, 10);

        if (Number.isFinite(activeSlideIndex)) return activeSlideIndex;
        if (Number.isFinite(swiper?.realIndex)) return swiper.realIndex;

        const activeButton = buttons.find((button) => button.classList.contains('swiper-slide-thumb-active'));
        const fallbackIndex = Number.parseInt(activeButton?.dataset.index, 10);

        return Number.isFinite(fallbackIndex) ? fallbackIndex : 0;
      }

      updateMobileThumbnailDots() {
        const thumbnailsContainer = this.querySelector('.swiper-controls__thumbnails-container[thumbnails]');
        if (!thumbnailsContainer) return;

        const buttons = Array.from(thumbnailsContainer.querySelectorAll('.swiper-controls__thumbnail[data-index]'));
        if (!buttons.length) return;

        const isMobileProductPage =
          window.matchMedia('(max-width: 749px)').matches &&
          document.body.classList.contains('template-product--new-product-page');

        if (!isMobileProductPage || buttons.length <= 5) {
          thumbnailsContainer.style.removeProperty('--fv-mobile-dots-offset');
          buttons.forEach((button) => {
            button.style.removeProperty('--fv-mobile-dot-order');
          });
          return;
        }

        const total = buttons.length;
        const rawActiveIndex = this.getMobileDotsActiveIndex(buttons);
        const activeIndex = Math.min(Math.max(rawActiveIndex, 0), total - 1);
        const startIndex = Math.min(Math.max(activeIndex - 2, 0), total - 5);
        const dotStep = 16;

        thumbnailsContainer.style.setProperty('--fv-mobile-dots-offset', `${startIndex * dotStep * -1}px`);

        buttons.forEach((button) => {
          const index = Number.parseInt(button.dataset.index, 10);
          button.style.setProperty('--fv-mobile-dot-order', Number.isFinite(index) ? index : 0);
        });
      }

      setActiveMedia(mediaId, smooth = false) {
        if (!mediaId) return false;

        let cleanMediaId = mediaId;
        if (mediaId.indexOf('-') !== -1) {
          const parts = mediaId.split('-');
          cleanMediaId = parts[parts.length - 1];
        }

        let element = this.querySelector(`[data-media-id="${cleanMediaId}"]`);
        
        if (!element) {
          element = this.querySelector(`[data-media-id="${mediaId}"]`);
        }

        if (!element) return false;

        this.querySelectorAll('[data-media-id].active').forEach((node) => node.classList.remove('active'));

        element.classList.add('active');

        const allMediaElements = Array.from(this.querySelectorAll('[data-media-id]'));
        const mediaIndex = allMediaElements.indexOf(element);

        this.syncSlideshow(element, mediaIndex, smooth);
        this.syncThumbnails(element, mediaIndex);
        this.updateMobileThumbnailDots();

        if (this.classList.contains('media-gallery--grid')) {
          this.swapGridMedia(element, mediaIndex);
        }

        return true;
      }

      /**
       * Sync slideshow/swiper component when media is activated
       * @param {HTMLElement} mediaElement - The active media element
       * @param {number} mediaIndex - The index of the media in the gallery
       * @param {boolean} smooth - Whether to use smooth transition
       */
      syncSlideshow(mediaElement, mediaIndex, smooth = false) {
        try {
          if (!this.classList.contains('media-gallery--carousel')) return;

          const swiperComponent = this.querySelector('swiper-component');
          if (!swiperComponent) return;

          const waitForSwiper = (retries = 10) => {
            let swiper = swiperComponent.initSwiper || swiperComponent.swiperEl?.swiper;

            if (!swiper && retries === 10 && typeof swiperComponent.initializeSwiper === 'function') {
              swiperComponent.initializeSwiper();
              swiper = swiperComponent.initSwiper || swiperComponent.swiperEl?.swiper;
            }

            if (!swiper) {
              if (retries > 0) setTimeout(() => waitForSwiper(retries - 1), 50);
              return;
            }

            const slides = Array.from(swiper.slides || []);
            const slideElement = mediaElement.closest('.swiper-slide');
            let slideIndex = slideElement ? slides.indexOf(slideElement) : mediaIndex;

            if (slideIndex < 0) slideIndex = mediaIndex;
            if (slideIndex < 0 || slideIndex >= slides.length) return;

            const realIndex = Number.parseInt(slideElement?.dataset.swiperSlideIndex, 10);
            const speed = smooth ? 800 : 0;

            if (swiper.params?.loop && Number.isFinite(realIndex) && typeof swiper.slideToLoop === 'function') {
              swiper.slideToLoop(realIndex, speed);
            } else if (typeof swiper.slideTo === 'function') {
              swiper.slideTo(slideIndex, speed);
            }
          };

          waitForSwiper();
        } catch (e) {
          try {
            if (this.slideshow && typeof this.slideshow.goTo === 'function') {
              this.slideshow.goTo(mediaIndex);
            }
          } catch (err) {
            // silent
          }
        }
      }
      /**
       * Sync thumbnails controls when media is activated
       * @param {HTMLElement} mediaElement - The active media element
       * @param {number} mediaIndex - The index of the media in the gallery
       */
      syncThumbnails(mediaElement, mediaIndex) {
        try {
          const thumbnailsContainer = this.querySelector('.swiper-controls__thumbnails-container');
          if (!thumbnailsContainer) return;

          const thumbnailButtons = Array.from(thumbnailsContainer.querySelectorAll('.swiper-controls__thumbnail[data-index]'));
          if (thumbnailButtons.length === 0) return;

          let slideIndex = mediaIndex;
          
          if (this.classList.contains('media-gallery--carousel')) {
            const swiperComponent = this.querySelector('swiper-component');
            if (swiperComponent) {
              const slideElement = mediaElement.closest('.swiper-slide');
              if (slideElement) {
                const slides = Array.from(swiperComponent.querySelectorAll('.swiper-wrapper .swiper-slide'));
                const foundIndex = slides.indexOf(slideElement);
                if (foundIndex >= 0) {
                  slideIndex = foundIndex;
                }
              } else {
                if (swiperComponent.initializeSwiper && typeof swiperComponent.initializeSwiper.activeIndex === 'number') {
                  slideIndex = mediaIndex;
                }
              }
            }
          }

          let targetThumbnail = thumbnailButtons.find((button) => {
            const index = parseInt(button.getAttribute('data-index'), 10);
            return !isNaN(index) && index === slideIndex;
          });

          if (!targetThumbnail) {
            targetThumbnail = thumbnailButtons.find((button) => {
              const index = parseInt(button.getAttribute('data-index'), 10);
              return !isNaN(index) && index === mediaIndex;
            });
          }

          if (targetThumbnail) {
            const finalSlideIndex = parseInt(targetThumbnail.getAttribute('data-index'), 10);

            this.syncThumbnailSwiper(thumbnailsContainer, thumbnailButtons, targetThumbnail, finalSlideIndex);
          }
        } catch (e) {
          // silent
        }
      }

      /**
       * Sync thumbnail swiper to show the active thumbnail
       * @param {HTMLElement} thumbnailsContainer - The thumbnail container element
       * @param {Array} thumbnailButtons - Array of all thumbnail buttons
       * @param {HTMLElement} targetThumbnail - The active thumbnail button
       * @param {number} slideIndex - The slide index to sync to
       */
      syncThumbnailSwiper(thumbnailsContainer, thumbnailButtons, targetThumbnail, slideIndex) {
        try {
          const waitForThumbnailSwiper = (retries = 10) => {
            const swiperComponent = this.querySelector('swiper-component');
            let thumbsSwiper = swiperComponent?._thumbsSwiper || swiperComponent?.initSwiper?.thumbs?.swiper || null;

            if (!thumbsSwiper) {
              const thumbnailSwiper = thumbnailsContainer.querySelector('.swiper');
              if (thumbnailSwiper && thumbnailSwiper.swiper) {
                thumbsSwiper = thumbnailSwiper.swiper;
              }
            }

            if (thumbsSwiper) {
              if (slideIndex >= 0 && slideIndex < thumbnailButtons.length) {
                thumbsSwiper.slideTo(slideIndex, 300);
              }
            } else if (retries > 0) {
              setTimeout(() => waitForThumbnailSwiper(retries - 1), 50);
            }
          };

          waitForThumbnailSwiper();
        } catch (e) {
          // silent
        }
      }

      /**
       * Swap media in grid layout: move variant image to first position
       * @param {HTMLElement} mediaElement - The active media element
       * @param {number} mediaIndex - The index of the media in the gallery
       */
      swapGridMedia() {
        try {
          if (typeof MainEvents !== 'undefined' && MainEvents.variantUpdate) {
            const handleVariantUpdateOnce = (event) => {
              try {
                const source = event?.detail?.data?.html;
                if (!source) return;

                const newMediaGallery = source.querySelector('media-gallery');
                if (!newMediaGallery) return;
                this.replaceWith(newMediaGallery);

                try {
                  newMediaGallery.scrollIntoView({ behavior: 'smooth' });
                } catch (e) {
                  // ignore scrolling errors
                }
              } catch (e) {
                // silent
              } finally {
                document.removeEventListener(MainEvents.variantUpdate, handleVariantUpdateOnce);
              }
            };
            document.addEventListener(MainEvents.variantUpdate, handleVariantUpdateOnce);
          }
          
        } catch (e) {
          // silent
        }
      }
    }
  );
}
